import {
  ClickAwayListener,
  Grow,
  Paper,
  PaperProps,
  Popper,
  PopperProps,
  Stack,
  SxProps,
  Theme,
  styled,
} from '@mui/material'
import cx from 'classnames'
import noop from 'lodash/noop'
import { CSSProperties, FC, PropsWithChildren, ReactNode, useEffect } from 'react'

import { withStopPropagation } from '../../../utils/component/stopPropagation'

import MenuHeader from './MenuHeader'

export { default as MenuHeader } from './MenuHeader'
export { default as MenuItem } from './MenuItem'
export { default as MenuListItem } from './MenuListItem'

export interface MenuProps {
  anchorEl: PopperProps['anchorEl']
  disablePortal?: boolean
  divider?: boolean
  header?: ReactNode
  keepMounted?: boolean
  open: boolean
  paperProps?: Partial<PaperProps>
  placement?: PopperProps['placement']
  popperProps?: Partial<Omit<PopperProps, 'anchorEl' | 'open' | 'placement'>>
  showHeader?: boolean
  stopPropagation?: boolean
  sx?: SxProps<Theme>
  onClose: () => void
}

const Menu: FC<PropsWithChildren<MenuProps>> = ({
  anchorEl,
  children,
  disablePortal,
  divider = true,
  header,
  keepMounted,
  open,
  paperProps,
  placement,
  popperProps,
  showHeader,
  stopPropagation,
  sx,
  onClose,
}) => {
  const onClickAway = (event: MouseEvent) => {
    if ((event.target as Element).closest('.disable-click-away')) {
      return
    }
    onClose()
  }

  useEffect(() => {
    if (open) {
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onClose()
        }
      }

      window.addEventListener('keydown', onKeyDown)

      return () => window.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose, open])

  return (
    <Popper
      anchorEl={anchorEl}
      disablePortal={disablePortal}
      open={open}
      placement={placement}
      popperOptions={{
        modifiers: [{ name: 'offset', options: { offset: [0, 4] } }],
      }}
      role="menu"
      keepMounted={keepMounted}
      transition
      {...popperProps}
      sx={{ zIndex: 9999, ...popperProps?.sx }}
    >
      {({ TransitionProps, placement }) => (
        <Grow {...TransitionProps} style={{ transformOrigin: getTransformOrigin(placement) }}>
          <StyledPaper
            {...paperProps}
            className={cx({ 'dp-divider': divider }, paperProps?.className)}
            elevation={4}
          >
            <ClickAwayListener mouseEvent="onMouseUp" onClickAway={onClickAway}>
              <Container
                className={cx({ 'dp-hasheader': header || showHeader })}
                sx={sx}
                onClick={stopPropagation ? withStopPropagation(noop) : undefined}
              >
                {header ?? (showHeader && <MenuHeader onClose={onClose} />)}

                {children}
              </Container>
            </ClickAwayListener>
          </StyledPaper>
        </Grow>
      )}
    </Popper>
  )
}

const getTransformOrigin = (
  placement: PopperProps['placement'],
  transformOrigin?: CSSProperties['transformOrigin']
): CSSProperties['transformOrigin'] => {
  if (placement.endsWith('-end') && !transformOrigin) {
    return getTransformOrigin(placement, 'right')
  }

  if (placement.endsWith('-start') && !transformOrigin) {
    return getTransformOrigin(placement, 'left')
  }

  if (placement.startsWith('bottom')) {
    return [transformOrigin, 'top'].join(' ')
  }

  if (placement.startsWith('top')) {
    return [transformOrigin, 'bottom'].join(' ')
  }
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(1),
}))

const Container = styled(Stack)`
  &.dp-divider .MuiList-root .MuiMenuItem-root:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.palette.grey[100]};
  }

  &.dp-hasheader .MuiList-root {
    padding-top: 0;
  }
`

export default Menu
